import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../utils/cn';

export const Carousel = ({ items, renderItem, className }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);

    const slideVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0
        })
    };

    const swipeConfidenceThreshold = 10000;
    const swipePower = (offset, velocity) => {
        return Math.abs(offset) * velocity;
    };

    const paginate = (newDirection) => {
        setDirection(newDirection);
        let nextIndex = currentIndex + newDirection;
        if (nextIndex < 0) nextIndex = items.length - 1;
        if (nextIndex >= items.length) nextIndex = 0;
        setCurrentIndex(nextIndex);
    };

    if (!items || items.length === 0) return null;

    return (
        <div className={cn("relative w-full flex flex-col items-center", className)}>
            <div className="relative w-full overflow-visible flex items-stretch justify-center">
                <AnimatePresence initial={false} custom={direction} mode="wait">
                    <motion.div
                        key={currentIndex}
                        custom={direction}
                        variants={slideVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "tween", duration: 0.15, ease: "easeInOut" },
                            opacity: { duration: 0.1 }
                        }}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={1}
                        onDragEnd={(e, { offset, velocity }) => {
                            const swipe = swipePower(offset.x, velocity.x);

                            if (swipe < -swipeConfidenceThreshold) {
                                paginate(1);
                            } else if (swipe > swipeConfidenceThreshold) {
                                paginate(-1);
                            }
                        }}
                        className="w-full"
                    >
                        {renderItem(items[currentIndex], currentIndex)}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-4 mt-6 z-10">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(-1)}
                    className="rounded-full w-8 h-8 p-0 flex items-center justify-center"
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex gap-2">
                    {items.map((_, idx) => (
                        <button
                            key={idx}
                            onClick={() => {
                                setDirection(idx > currentIndex ? 1 : -1);
                                setCurrentIndex(idx);
                            }}
                            className={cn(
                                "w-1.5 h-1.5 rounded-full transition-all duration-200",
                                idx === currentIndex ? "bg-primary-600 w-4" : "bg-slate-300 hover:bg-slate-400"
                            )}
                        />
                    ))}
                </div>

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => paginate(1)}
                    className="rounded-full w-8 h-8 p-0 flex items-center justify-center"
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
            </div>

            <div className="text-xs text-slate-400 mt-2">
                {currentIndex + 1} of {items.length}
            </div>
        </div>
    );
};
